import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { createSelector } from 'reselect';
import { showCohortEvents, showConversionEvents } from '../customEvents/utils';
import { SAN_PARTNERS } from '../partnerConfiguration/consts';
import { PERMISSION_KEY_ASSIST } from '../reducers/reportsConfig/baseReportConfig';
import { getOrgAgencies } from '../selectors/agencies';
import { getFields, getFilters } from '../selectors/fields';
import { getTranslate } from '../selectors/locale';
import rules from '../selectors/reportsConfig/fieldVisibilityRules';
import { getAdminModeEnabled, getUserData, getUserPermissionDimensions } from '../selectors/user';
import {
    AGENCY_PERMISSION_DIMENSIONS,
    COUNTRY_DIMENSION,
    dataRestrictionsStateToApiFormat,
    SDK_EVENT_DIMENSION,
} from '../utils/agencyPermissions';
import { DEFAULT_SECTION_ACCESS_TYPE, SECTION_ACCESS_TYPES, SECTIONS } from '../utils/consts';
import { IN_OPERATOR, NOT_IN_OPERATOR } from '../utils/reportsConsts';
import {
    ALWAYS_DISABLED_SCREENS,
    calculateRemovedItems,
    DELETABLE_GROUPS,
    DIMENSION_STEPS,
    EXCLUDED_METRICS_SELECTION_PAGE,
    getStateFromUserDetails,
    getUserDisplayName,
    getUserRole,
    HIDDEN_SCREENS,
    INITIAL_FIELD_TYPES,
    MANAGE_PERMISSIONS_STEPS,
    MEMBER_GROUP_TYPES,
    MEMBER_TYPES,
    ORGANIC_SOURCE_NAME,
    RESTRICTED_ROLES,
    SCREENS_REQUIRING_ALL_METRICS,
    SPECIAL_COUNTRIES,
    STEPS_BY_MEMBER_TYPE,
} from './utils';

const getPageLoaded = state => state.teamManagement.pageLoaded;
const getUsers = state => state.teamManagement.orgUsers;
const getMessages = state => state.teamManagement.messages;
const getMessagesDefs = state => state.teamManagement.messagesDefs;
const getPage = state => state.teamManagement.page;
const getAgencyDialog = state => state.teamManagement.agencyDialog;
const getEditMemberId = state => state.teamManagement.editMemberId;
const getUserRegistrationForm = state => state.teamManagement.userRegistrationForm;
const getRoleSelectionForm = state => state.teamManagement.roleSelectionForm;
const getAgencySelectionForm = state => state.teamManagement.agencySelectionForm;
const getSelectedMetrics = state => state.teamManagement.selectedMetrics;
const getScreenSelectionForm = state => state.teamManagement.screenSelectionForm;
const getSaveMemberError = state => state.teamManagement.saveMemberError;
const getScreensDict = state => state.teamManagement.screensDict;
const getSelectedDimensionPermissionValues = state => state.teamManagement.dimensionFilters;
const getDataRestrictions = state => state.teamManagement.dataRestrictions;
export const getSectionPermissions = state => state.teamManagement.sectionPermissions;
const getSectionsDict = state => state.teamManagement.sectionsDict;
const getConfigurationRequiredDialog = state => state.teamManagement.configurationRequiredDialog;
export const getActiveMemberType = state => state.teamManagement.activeMemberType;
export const getAppsList = state => state.teamManagement.appsList;
export const getPartners = state => state.teamManagement.partners;
export const getConfirmChangesDialogState = state => state.teamManagement.confirmChangesDialog;
export const getSdkEvents = state => state.teamManagement.sdkEvents;
const getInitialPermissionSnapshot = state => state.teamManagement.initialPermissionSnapshot;
const getSavedMemberName = state => state.teamManagement.savedMemberName;

const getFieldTypes = createSelector(
    [getUserData, getActiveMemberType, getAgencySelectionForm, getOrgAgencies],
    (userData, activeMemberType, agencySelectionForm, orgAgencies) => {
        const {
            event_slot_analysis_types: eventSlotAnalysisTypes = [],
            feature_flags: featureFlags = [],
            permission_keys: permissionKeys = [],
        } = userData || {};

        const isEditingAgency = activeMemberType === MEMBER_TYPES.AGENCY && agencySelectionForm?.id;
        let permissionKeysToUse = permissionKeys;
        if (isEditingAgency) {
            const selectedAgency = orgAgencies?.find(a => a.agency_id === agencySelectionForm.id);
            if (selectedAgency) {
                permissionKeysToUse = selectedAgency.permission_keys;
            } else {
                permissionKeysToUse = agencySelectionForm.permission_keys || [];
            }
        }

        const fieldTypes = [];

        if (showCohortEvents(eventSlotAnalysisTypes)) {
            fieldTypes.push({ type: 'customEvents', label: 'STATIC.CUSTOM_EVENTS' });
        }
        if (showConversionEvents(eventSlotAnalysisTypes)) {
            fieldTypes.push({ type: 'conversionEvents', label: 'STATIC.CONVERSION_EVENTS' });
        }
        if (featureFlags && featureFlags.includes('cross-device')) {
            fieldTypes.push({ type: 'crossDeviceMetrics', label: 'STATIC.CROSS_DEVICE_METRICS' });
        }
        if (featureFlags && featureFlags.includes('web_customer')) {
            fieldTypes.push({ type: 'webMetrics', label: 'STATIC.WEB_METRICS' });
            fieldTypes.push({ type: 'appMetrics', label: 'STATIC.APP_METRICS' });
        }
        if (permissionKeysToUse && permissionKeysToUse.includes(PERMISSION_KEY_ASSIST)) {
            fieldTypes.push({ type: 'assistMetrics', label: 'STATIC.ASSIST_METRICS' });
        }
        return INITIAL_FIELD_TYPES.concat(fieldTypes);
    }
);

const _buildUserSearchString = user => {
    const displayName = getUserDisplayName(user);
    if (user.role && user.role.description) {
        return `${displayName};${user.username};${user.first_name};${user.last_name};${user.email};${
            user.role.description
        };${getUserRole(user)}`.toLowerCase();
    }
    return `${displayName};${user.username};${user.first_name};${user.last_name};${user.email};${getUserRole(
        user
    )}`.toLowerCase();
};

const getUserGroup = userStatus => {
    if (userStatus === 'Member') {
        return MEMBER_GROUP_TYPES.active;
    }

    if (userStatus === 'invite_sent' || userStatus === 'invite_opened') {
        return MEMBER_GROUP_TYPES.pending;
    }

    return MEMBER_GROUP_TYPES.inactive;
};

const getUsersByGroup = createSelector([getUsers, getTranslate, getUserData], (users, translate, userData) => {
    const groups = users.reduce(
        (total, user) => {
            const group = getUserGroup(user.status);
            const isReInviteLessThan24h =
                user.last_re_invited_at && moment().diff(moment(user.last_re_invited_at * 1000), 'hours') < 24;
            const userId = user.organization_user_id || user.username;
            const isMe = userId === userData.organization_user_id;

            const newUser = {
                ...user,
                id: userId,
                isMe,
                name: getUserDisplayName(user),
                description: user.role && user.role.description ? user.role.description : '',
                roleText: getUserRole(user),
                searchText: _buildUserSearchString(user),
                deletable: DELETABLE_GROUPS.includes(group),
                disabled: isReInviteLessThan24h || user.disabled,
                disabledTootlip:
                    isReInviteLessThan24h || user.disabled
                        ? translate('STATIC.PAGES.TEAM_MEMBERS.REINVITE_DISABLED_TOOLTIP')
                        : '',
            };
            total[group].push(newUser);
            return total;
        },
        {
            [MEMBER_GROUP_TYPES.pending]: [],
            [MEMBER_GROUP_TYPES.active]: [],
            [MEMBER_GROUP_TYPES.inactive]: [],
        }
    );

    // sort pending list according to creation time.
    groups.pending.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return groups;
});

const getAgenciesByGroup = createSelector([getOrgAgencies], agencies => {
    return {
        [MEMBER_GROUP_TYPES.active]: (agencies || []).map(agency => ({
            id: agency.agency_id,
            name: agency.display_name,
            imgSrc: agency.company_logo,
            role: agency.role,
            permission_keys: agency.permission_keys,
            deletable: true,
            searchText: `${agency.display_name};${agency.agency_id}`.toLowerCase(),
        })),
    };
});

const getMemberTypes = createSelector(
    [getUserData, getUsersByGroup, getAgenciesByGroup],
    (userData, usersByGroup, agenciesByGroup) => {
        const categories = [{ type: MEMBER_TYPES.USER, count: usersByGroup.active?.length || 0 }];
        if (userData.is_agencies_allowed) {
            categories.push({ type: MEMBER_TYPES.AGENCY, count: agenciesByGroup.active?.length || 0 });
        }
        return categories;
    }
);

const getMembersByGroup = createSelector(
    [getUsersByGroup, getAgenciesByGroup, getActiveMemberType],
    (users, agencies, activeMemberType) => (activeMemberType === MEMBER_TYPES.USER ? users : agencies)
);

const getGroups = createSelector(
    [getMembersByGroup, getMessages, getMessagesDefs, getAdminModeEnabled, getActiveMemberType],
    (membersByGroup, messages, messagesDefs, adminModeEnabled, activeMemberType) => {
        return Object.keys(membersByGroup)
            .filter(group => {
                if (!adminModeEnabled && group === MEMBER_GROUP_TYPES.inactive) {
                    return false;
                }

                if (activeMemberType === MEMBER_TYPES.AGENCY && group === MEMBER_GROUP_TYPES.active) {
                    return true;
                }

                return membersByGroup[group].length;
            })
            .map(group => ({
                name: group,
                displayName: `STATIC.PAGES.TEAM_MEMBERS.GROUP_NAME_${group.toUpperCase()}_${activeMemberType}`,
                infoMessages: messages[group] ? messages[group].map(messageId => messagesDefs[messageId]) : [],
            }));
    }
);

export const getBaseSources = createSelector(
    [getPartners],
    partners => {
        const mappedSources = partners.map(baseSource => ({
            id: baseSource.id || baseSource.name,
            name: baseSource.name,
            displayName: baseSource.displayName,
            icon: baseSource.logoPath,
        }));
        return uniqBy(mappedSources, 'name');
    }
);

export const getPermissionDimensions = createSelector(
    [
        getUserPermissionDimensions,
        getFilters,
        getSelectedDimensionPermissionValues,
        getAppsList,
        getBaseSources,
        getActiveMemberType,
    ],
    (userPermissionDimensions, apiFilters, selectedValues, apps, baseSources, activeMemberType) => {
        const genericDimensions = userPermissionDimensions.filter(
            d => !AGENCY_PERMISSION_DIMENSIONS.includes(d) && d !== 'app'
        );
        const memberPermissionDimensions =
            activeMemberType === MEMBER_TYPES.AGENCY
                ? [...AGENCY_PERMISSION_DIMENSIONS, ...genericDimensions]
                : userPermissionDimensions;

        const permissionDimensions = {};
        memberPermissionDimensions.forEach(dimensionName => {
            // App values are stored by id and not name
            if (dimensionName === 'app') {
                permissionDimensions.app = {
                    name: 'app',
                    values: (apps || []).map(app => ({
                        name: app.id,
                        displayName: app.name,
                        icon: app.icon,
                    })),
                    selected: selectedValues.app || { values: [], addFuture: false },
                };
                return;
            }

            // App site values are stored by id and not name
            if (dimensionName === 'app_site_id') {
                permissionDimensions.app_site_id = {
                    name: 'app_site_id',
                    values: (apps || []).flatMap(app =>
                        app.sites.map(site => ({
                            name: site.publicId,
                            displayName: site.name,
                            platform: site.platform,
                            app: {
                                name: app.id,
                                displayName: app.name,
                                icon: app.icon,
                            },
                        }))
                    ),
                    selected: selectedValues.app_site_id || { values: [], addFuture: false },
                };
                return;
            }

            if (dimensionName === 'base_source') {
                permissionDimensions.base_source = {
                    name: 'base_source',
                    values: baseSources,
                    selected: selectedValues.base_source || { values: [], addFuture: false },
                };
                return;
            }

            // All other dimensions we can filter on must have an API filter
            const filter = apiFilters.find(f => f.name === dimensionName);
            if (filter) {
                const filterValues =
                    dimensionName === COUNTRY_DIMENSION
                        ? filter.values.filter(v => !SPECIAL_COUNTRIES.includes(v.name))
                        : filter.values;
                permissionDimensions[dimensionName] = {
                    name: dimensionName,
                    displayName: filter.display_name,
                    values: filterValues.map(v => ({ name: v.name, displayName: v.display_name })),
                    selected: selectedValues[dimensionName] || { values: [], addFuture: false },
                };
            }
        });
        return permissionDimensions;
    }
);

export const getPermissionDimension = dimensionName => {
    return createSelector([getPermissionDimensions], permissionDimensions => {
        return permissionDimensions[dimensionName];
    });
};

export const getPageSteps = createSelector(
    [getPermissionDimensions, getRoleSelectionForm, getActiveMemberType],
    (permissionDimensions, roleSelectionForm, activeMemberType) => {
        const { baseSteps, finalSteps } = STEPS_BY_MEMBER_TYPE[activeMemberType];
        const isUser = activeMemberType === MEMBER_TYPES.USER;

        const pageStepByDimension = {
            app: MANAGE_PERMISSIONS_STEPS.apps,
            app_site_id: MANAGE_PERMISSIONS_STEPS.apps,
            base_source: MANAGE_PERMISSIONS_STEPS.sources,
        };

        if (isUser && !RESTRICTED_ROLES.includes(roleSelectionForm.role)) {
            return [...baseSteps, MANAGE_PERMISSIONS_STEPS.review];
        }

        const permissionSteps = Object.values(permissionDimensions).map(
            dim => pageStepByDimension[dim.name] || dim.name
        );

        const genericDimensionSteps = permissionSteps.filter(
            step => !Object.values(MANAGE_PERMISSIONS_STEPS).includes(step)
        );

        const activeDimensionSteps = DIMENSION_STEPS.filter(step => permissionSteps.includes(step));

        return [...baseSteps, ...activeDimensionSteps, ...genericDimensionSteps, ...finalSteps];
    }
);

export const getEditedMember = createSelector([getEditMemberId, getMembersByGroup], (editMemberId, membersByGroup) => {
    if (!editMemberId) return null;

    const activeMembers = membersByGroup[MEMBER_GROUP_TYPES.active];
    return activeMembers.find(member => member.id === editMemberId);
});

export const getTeamManagementPage = createSelector(
    [
        getPageLoaded,
        getMembersByGroup,
        getGroups,
        getMemberTypes,
        getActiveMemberType,
        getPage,
        getAgencyDialog,
        getOrgAgencies,
        getConfirmChangesDialogState,
        getSavedMemberName,
    ],
    (
        pageLoaded,
        membersByGroup,
        membersGroups,
        memberTypes,
        activeMemberType,
        page,
        agencyDialog,
        agencies,
        { open: confirmChangesDialogOpen },
        savedMemberName
    ) => {
        return {
            pageLoaded: pageLoaded && !!agencies,
            membersByGroup,
            membersGroups,
            memberTypes,
            activeMemberType,
            page,
            agencyDialog,
            confirmChangesDialogOpen,
            savedMemberName,
        };
    }
);

export const getAppPageStep = createSelector(
    [
        getAppsList,
        getPermissionDimension('app_site_id'),
        getPermissionDimension('app'),
        getTranslate,
        getInitialPermissionSnapshot,
        getActiveMemberType,
    ],
    (apps, appSiteDimension, appDimension, translate, initialPermissionSnapshot, activeMemberType) => {
        const isUser = activeMemberType === MEMBER_TYPES.USER;

        if (isUser) {
            const selectedAppIds = appDimension?.selected.values || [];
            return {
                dimensionType: 'app',
                apps: apps
                    .map(app => ({
                        ...app,
                        selected: selectedAppIds.includes(app.id),
                        sites: [],
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
                translate,
                removedAppSites: [],
                activeMemberType,
            };
        }

        const allAppSiteIds = apps.flatMap(app => app.sites.map(site => site.publicId));
        const currentAppSites = appSiteDimension?.selected.values || [];
        const removedAppSites = calculateRemovedItems(
            initialPermissionSnapshot,
            'app_site_id',
            allAppSiteIds,
            currentAppSites
        );

        return {
            dimensionType: 'app_site_id',
            apps: apps
                .map(app => ({
                    ...app,
                    selected: app.sites.every(site =>
                        (appSiteDimension?.selected.values || []).includes(site.publicId)
                    ),
                    sites: app.sites.map(site => ({
                        ...site,
                        selected: (appSiteDimension?.selected.values || []).includes(site.publicId),
                    })),
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
            translate,
            removedAppSites,
            activeMemberType,
        };
    }
);

export const getDisabledSourceNames = createSelector(
    [getActiveMemberType, getUserData],
    (activeMemberType, userData) => {
        return activeMemberType === MEMBER_TYPES.AGENCY && !userData.san_partners_for_agency_supported
            ? Array.from(SAN_PARTNERS)
            : [];
    }
);

export const getSourcePageStep = createSelector(
    [
        getBaseSources,
        getPermissionDimension('base_source'),
        getMembersByGroup,
        getGroups,
        getAgencySelectionForm,
        getAdminModeEnabled,
        getDisabledSourceNames,
        getTranslate,
        getInitialPermissionSnapshot,
        getActiveMemberType,
    ],
    (
        baseSources,
        permissionDimension,
        membersByGroup,
        membersGroups,
        agencySelectionForm,
        adminModeEnabled,
        disabledSourceNames,
        translate,
        initialPermissionSnapshot,
        activeMemberType
    ) => {
        const isAgency = activeMemberType === MEMBER_TYPES.AGENCY;
        // "In use by" is only relevant for agencies
        let membersPerSource = {};
        if (isAgency) {
            const members = membersGroups.reduce((acc, group) => {
                if (
                    group.name === MEMBER_GROUP_TYPES.pending ||
                    (!adminModeEnabled && group.name === MEMBER_GROUP_TYPES.inactive)
                ) {
                    return acc;
                }

                return [...acc, ...membersByGroup[group.name]];
            }, []);

            membersPerSource = members.reduce((acc, member) => {
                if (member.name === agencySelectionForm.name) {
                    return acc;
                }

                const rowPermission = member.role.row.find(({ dimension }) => dimension === 'base_source') || {
                    operator: 'not in',
                    values: [],
                };

                const sourcesToAdd =
                    rowPermission.operator === IN_OPERATOR
                        ? rowPermission.values
                        : baseSources
                              .filter(source => !rowPermission.values.includes(source.name))
                              .map(source => source.name);

                sourcesToAdd.forEach(sourceName => {
                    acc[sourceName] = acc[sourceName] || [];
                    acc[sourceName].push(member.name);
                });
                return acc;
            }, {});
        }

        const allSourceNames = baseSources.map(p => p.name);
        const currentSources = permissionDimension?.selected.values || [];
        const removedSources = isAgency
            ? calculateRemovedItems(initialPermissionSnapshot, 'base_source', allSourceNames, currentSources)
            : [];

        const selectedValues = permissionDimension?.selected.values || [];

        const sources = baseSources
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(source => ({
                ...source,
                selected: selectedValues.includes(source.name),
                id: source.name,
                disabled: disabledSourceNames.includes(source.name),
            }));

        return {
            topSourceNames: [ORGANIC_SOURCE_NAME],
            sources,
            membersPerSource,
            translate,
            removedSources,
            activeMemberType,
        };
    }
);

export const getSectionsPageStep = createSelector(
    [getSectionPermissions, getSectionsDict, getRoleSelectionForm],
    (allSectionPermissions, sectionsDict, roleSelectionForm) => {
        return {
            allSectionPermissions,
            allSections: sectionsDict[roleSelectionForm.role],
        };
    }
);

export const getSdkEventStepCard = createSelector(
    [getSdkEvents, getDataRestrictions, getTranslate, getActiveMemberType],
    (sdkEvents, dataRestrictions, translate, activeMemberType) => {
        const sdkEventRestriction = dataRestrictions?.[SDK_EVENT_DIMENSION] || {};
        const selectedEventNames = sdkEventRestriction.values || [];
        const addFuture = sdkEventRestriction.addFuture || false;

        return {
            events: (sdkEvents || []).map(event => ({
                ...event,
                selected: selectedEventNames.includes(event.eventName),
            })),
            selectedEventNames,
            addFuture,
            translate,
            activeMemberType,
            loading: sdkEvents === null, // null = not loaded yet, [] = loaded but empty
        };
    }
);

export const getRolePageStep = createSelector(
    [getPage, getRoleSelectionForm, getUserData, getTranslate, getInitialPermissionSnapshot],
    (page, roleForm, userData, translate, initialPermissionSnapshot) => ({
        mode: page.mode,
        role: roleForm.role,
        showRestrictedMode: userData.is_permissions_premium,
        translate,
        initialRole: initialPermissionSnapshot ? initialPermissionSnapshot.role.type : roleForm.role,
    })
);

export const getUserDetailsPageStep = createSelector(
    [getUserData, getUserRegistrationForm, getSaveMemberError, getPage, getTranslate],
    (userData, userRegistrationForm, saveMemberError, page, translate) => ({
        username: userData.username,
        userRegistrationForm,
        saveMemberError,
        mode: page.mode,
        translate,
    })
);

export const getGenericDimensionPageStep = dimensionName => {
    return createSelector([getPermissionDimension(dimensionName), getTranslate], (permissionDimension, translate) => ({
        permissionDimension,
        translate,
    }));
};

export const getConfigurationRequiredDialogPage = createSelector(
    [getConfigurationRequiredDialog, getTranslate],
    ({ open, pages }, translate) => {
        return {
            open,
            pages,
            translate,
        };
    }
);

export const getFormattedFields = createSelector(
    [getFields, getFieldTypes, getSelectedMetrics],
    (fields, displayedFields, selectedMetrics) => {
        return displayedFields.map(field => ({
            ...field,
            values: (fields[field.type] || [])
                .filter(metric => rules.permissions(metric) && !EXCLUDED_METRICS_SELECTION_PAGE.includes(metric.name))
                .map(metric => ({
                    ...metric,
                    selected: selectedMetrics.values.includes(metric.name),
                })),
        }));
    }
);

export const getMetricsPageStep = createSelector(
    [getFormattedFields, getActiveMemberType],
    (fields, activeMemberType) => {
        return {
            fields,
            activeMemberType,
        };
    }
);

const getDisabledScreenNames = createSelector([getSelectedMetrics, getFormattedFields], (selectedMetrics, fields) => {
    const disabledScreens = [...ALWAYS_DISABLED_SCREENS];

    const allValues = fields.reduce((total, fieldType) => {
        total.push(...fieldType.values);
        return total;
    }, []);

    if (allValues.some(value => !value.selected) || !selectedMetrics.addFuture) {
        disabledScreens.push(...SCREENS_REQUIRING_ALL_METRICS);
    }

    return disabledScreens;
});

export const getScreens = createSelector(
    [getScreensDict, getTranslate, getRoleSelectionForm, getDisabledScreenNames, getScreenSelectionForm],
    (screensDict, translate, roleSelectionForm, disabledScreenNames, screenSelectionForm) => {
        const exportLogsTooltip = 'STATIC.PAGES.TEAM_MEMBERS.SCREENS.ENABLE_EXPORT_LOGS_WARNING';
        const dataDestinationsTooltip = 'STATIC.PAGES.TEAM_MEMBERS.SCREENS.DISABLE_DATA_DESTINATIONS_WARNING';
        const tooltip = {
            export_logs: exportLogsTooltip,
            'data-destinations': dataDestinationsTooltip,
        };

        const formatFeature = feature => {
            const shouldDisablePage = disabledScreenNames.includes(feature.name);
            const disabledTooltip = tooltip[feature.name];
            return {
                name: feature.name,
                displayName: feature.display_name,
                warning: shouldDisablePage ? null : feature.warning,
                disabled: shouldDisablePage,
                disabledTooltip: disabledTooltip ? translate(disabledTooltip) : null,
                selected: screenSelectionForm.selectedScreens.includes(feature.name),
            };
        };

        return (screensDict[roleSelectionForm.role] || []).map(section => ({
            name: section.name,
            displayName: section.display_name,
            children: section.features.filter(({ name }) => !HIDDEN_SCREENS.includes(name)).map(formatFeature),
        }));
    }
);

export const getScreenPageStep = createSelector(
    [getScreens, getScreenSelectionForm, getActiveMemberType, getSectionPermissions, getTranslate],
    (screens, screenSelectionForm, activeMemberType, sectionPermissions, translate) => ({
        screens,
        selectedScreens: screenSelectionForm.selectedScreens.filter(s => !HIDDEN_SCREENS.includes(s)),
        activeMemberType,
        translate,
        sectionPermissions,
    })
);

const getScreensForSummary = createSelector([getScreens, getScreenSelectionForm], (screens, { addFuture }) => {
    const values = [];
    screens.forEach(section => {
        const selectedScreensData = section.children.filter(screen => screen.selected);
        if (selectedScreensData.length) {
            values.push({
                ...section,
                children: selectedScreensData,
            });
        }
    });
    return {
        values,
        addFuture,
        overall: screens.flatMap(({ children }) => children).length,
    };
});

const getFieldsForSummary = createSelector([getFormattedFields, getSelectedMetrics], (fields, { addFuture }) => {
    const values = [];
    let overall = 0;
    fields.forEach(fieldType => {
        const selectedFields = fieldType.values.filter(f => f.selected);
        overall += fieldType.values.length;

        if (selectedFields.length) {
            values.push({
                ...fieldType,
                values: selectedFields,
            });
        }
    });
    return {
        values,
        addFuture,
        overall,
    };
});

const getSdkEventsForSummary = createSelector([getSdkEvents, getDataRestrictions], (sdkEvents, dataRestrictions) => {
    if (!sdkEvents) return null;
    const sdkEventRestriction = dataRestrictions?.[SDK_EVENT_DIMENSION] || {};
    const selectedEventNames = sdkEventRestriction.values || [];
    const addFuture = sdkEventRestriction.addFuture || false;

    const selectedEvents = sdkEvents.filter(event => selectedEventNames.includes(event.eventName));
    return {
        items: selectedEvents.map(event => ({
            name: event.eventName,
            displayName: event.displayName || event.eventName,
        })),
        selectedCount: selectedEvents.length,
        overall: new Set(sdkEvents.map(e => e.eventName)).size,
        addFuture,
    };
});

export const getReviewPageStep = createSelector(
    [
        getUserRegistrationForm,
        getPermissionDimensions,
        getScreensForSummary,
        getFieldsForSummary,
        getPage,
        getRoleSelectionForm,
        getActiveMemberType,
        getSectionPermissions,
        getPageSteps,
        getDisabledSourceNames,
        getSdkEventsForSummary,
    ],
    (
        registrationForm,
        permissionDimensions,
        screens,
        fields,
        page,
        roleSelectionForm,
        activeMemberType,
        sectionPermissions,
        pageSteps,
        disabledSourceNames,
        sdkEventsData
    ) => {
        const reviewStepIndex = pageSteps.indexOf(MANAGE_PERMISSIONS_STEPS.review);
        const pages = pageSteps.filter((step, index) => index < reviewStepIndex);

        const hasAttributionStep = pages.includes(MANAGE_PERMISSIONS_STEPS.attribution);
        const postbackPermission = sectionPermissions.find(
            p => p.name === SECTIONS.PARTNER_CONFIGURATION_IN_APP_EVENTS_POSTBACK
        );
        const postbackAccessType = postbackPermission?.access_type ?? DEFAULT_SECTION_ACCESS_TYPE;
        const hasPostbackEditAccess = hasAttributionStep && postbackAccessType === SECTION_ACCESS_TYPES.EDIT;

        return {
            registrationForm,
            permissionDimensions,
            screens,
            fields,
            mode: page.mode,
            roleSelectionForm,
            pages,
            pageSteps,
            activeMemberType,
            sectionPermissions,
            disabledSourceNames,
            sdkEventsData: hasPostbackEditAccess ? sdkEventsData : null,
        };
    }
);

const getAllSubItems = (values, subItemKey) => {
    return values.reduce((total, parent) => {
        return total.concat(parent[subItemKey]);
    }, []);
};

export const getSaveMemberData = createSelector(
    [
        getEditMemberId,
        getUserRegistrationForm,
        getAgencySelectionForm,
        getRoleSelectionForm,
        getFormattedFields,
        getSelectedMetrics,
        getScreens,
        getScreenSelectionForm,
        getPermissionDimensions,
        getActiveMemberType,
        getSectionPermissions,
        getDataRestrictions,
        getSdkEvents,
    ],
    (
        editMemberId,
        registrationForm,
        agencySelectionForm,
        roleForm,
        fields,
        selectedMetrics,
        screens,
        selectedScreens,
        permissionDimensions,
        activeMemberType,
        sectionPermissions,
        dataRestrictionsState,
        allSdkEvents
    ) => {
        const roleType = roleForm.role;

        const allEventNames = (allSdkEvents || []).map(e => e.eventName);
        const dataRestrictions =
            activeMemberType === MEMBER_TYPES.AGENCY
                ? dataRestrictionsStateToApiFormat(dataRestrictionsState, allEventNames)
                : [];

        return {
            registrationForm:
                activeMemberType === MEMBER_TYPES.USER
                    ? {
                          name: registrationForm.firstName.value,
                          lastname: registrationForm.lastName.value,
                          email: registrationForm.email.value,
                          description: registrationForm.description.value,
                      }
                    : {
                          ...agencySelectionForm,
                      },
            role: {
                type: roleType,
                description: registrationForm.description.value,
                row: RESTRICTED_ROLES.includes(roleType)
                    ? Object.values(permissionDimensions).map(permDim => ({
                          dimension: permDim.name,
                          values: !permDim.selected.addFuture
                              ? permDim.selected.values
                              : difference(
                                    permDim.values.map(x => x.name),
                                    permDim.selected.values
                                ),
                          operator: permDim.selected.addFuture ? NOT_IN_OPERATOR : IN_OPERATOR,
                      }))
                    : [],
                column: {
                    operator: selectedMetrics.addFuture ? NOT_IN_OPERATOR : IN_OPERATOR,
                    values: !selectedMetrics.addFuture
                        ? selectedMetrics.values
                        : difference(
                              getAllSubItems(fields, 'values').map(i => i.name),
                              selectedMetrics.values
                          ),
                },
                features: {
                    operator: selectedScreens.addFuture ? NOT_IN_OPERATOR : IN_OPERATOR,
                    access_new_features: selectedScreens.addFuture,
                    values: !selectedScreens.addFuture
                        ? selectedScreens.selectedScreens
                        : difference(
                              getAllSubItems(screens, 'children').map(x => x.name),
                              selectedScreens.selectedScreens
                          ),
                },
                sectionPermissions,
                dataRestrictions,
            },
            editMemberId,
        };
    }
);

export const getPermissionState = createSelector(
    [getInitialPermissionSnapshot, getSaveMemberData],
    (initialPermissionSnapshot, currentState) => ({
        startState: initialPermissionSnapshot || currentState,
        currentState,
    })
);

export const getHasPermissionChanges = createSelector(
    [getPermissionState],
    ({ startState, currentState }) => !isEqual(currentState, startState)
);

export const getManagePermissionsPage = createSelector(
    [
        getActiveMemberType,
        getAgencySelectionForm,
        getPage,
        getHasPermissionChanges,
        getPageSteps,
        getEditedMember,
        getPermissionDimensions,
        getRoleSelectionForm,
        getInitialPermissionSnapshot,
        getSaveMemberError,
    ],
    (
        activeMemberType,
        agencySelectionForm,
        page,
        hasChanges,
        pageSteps,
        editedMember,
        permissionDimensions,
        roleForm,
        initialPermissionSnapshot,
        saveMemberError
    ) => {
        const isUser = activeMemberType === MEMBER_TYPES.USER;
        const memberName = isUser ? (editedMember && editedMember.name) || '' : agencySelectionForm.name || '';

        const dimensionDisplayNames = Object.values(permissionDimensions).reduce((acc, dim) => {
            acc[dim.name] = dim.displayName || dim.name;
            return acc;
        }, {});

        const initialRole = initialPermissionSnapshot ? initialPermissionSnapshot.role.type : roleForm.role;
        const roleTypeChanged = initialRole !== roleForm.role;

        return {
            mode: page.mode,
            activeMemberType,
            page,
            hasChanges,
            pageSteps,
            memberName,
            dimensionDisplayNames,
            roleTypeChanged,
            saveMemberError,
        };
    }
);

export const managePermissionsData = createSelector(
    [getFormattedFields, getScreens, getPermissionDimensions, getSdkEvents],
    (fields, screens, permissionDimensions, sdkEventObjects) => {
        return {
            metrics: getAllSubItems(fields, 'values').map(i => i.name),
            screens: getAllSubItems(screens, 'children').map(i => i.name),
            permissionDimensions,
            sdkEventNames: (sdkEventObjects || []).map(e => e.eventName),
        };
    }
);

export const makeGetEditUserState = user =>
    createSelector([managePermissionsData], data => getStateFromUserDetails(user, data));
